import { performRefreshToken } from "@/helpers/refreshTokenManager";
import { accessTokenAtom, store } from "@/store";

// NOTE: Supports cases where `content-type` is other than `json`
const getBody = <T>(c: Response | Request): Promise<T> => {
  const contentType = c.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    return c.json();
  }

  if (contentType?.includes("application/pdf")) {
    return c.blob() as Promise<T>;
  }

  return c.text() as Promise<T>;
};

// NOTE: Update just base url
const getUrl = (contextUrl: string): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
  return baseUrl + contextUrl;
};

// NOTE: Add headers
const getHeaders = (headers?: HeadersInit): HeadersInit => {
  const accessToken = store.get(accessTokenAtom);

  return {
    ...headers,
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
};

export const customFetch = async <T>(
  url: string,
  options: RequestInit,
  isRetry = false
): Promise<T> => {
  const requestUrl = getUrl(url);
  const requestHeaders = getHeaders(options.headers);

  const requestInit: RequestInit = {
    ...options,
    headers: requestHeaders,
  };

  const response = await fetch(requestUrl, requestInit);

  // Check if token is expired and we haven't retried yet
  if (response.status === 401 && !isRetry) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData?.error === "Unauthorized") {
      // Refresh the token
      await performRefreshToken();
      // Retry the request with the new token
      return customFetch<T>(url, options, true);
    }
  }

  // Handle errors
  if (!response.ok) {
    const errorData = await getBody(response).catch(() => ({
      message: response.statusText,
    }));

    const error = new Error(
      (errorData as { message?: string })?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
    (error as ErrorType).status = response.status;
    (error as ErrorType).data = errorData;
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {
      data: undefined,
      status: response.status,
      headers: response.headers,
    } as T;
  }

  const data = await getBody<T>(response);

  return { status: response.status, data, headers: response.headers } as T;
};

export type ErrorType<T = unknown> = Error & {
  status: number;
  data?: T;
};
